import { c as _c } from "react/compiler-runtime";
import { type SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { clearSpartanProductHeroReady, markSpartanProductHeroReady } from '@/app/lcv/components/sections/spartan/products/shared/product-scroll/events';
import { safeCall } from '@/app/utils/safeCall';
interface UseHeroEntranceReadyOptions {
  failOpenMs?: number;
  hasCarMedia: boolean;
  hasCriticalMedia: boolean;
  hasEntranceReveal: boolean;
  onReady?: () => void;
}
const ENTRANCE_FAIL_OPEN_MS = 4000;
const decodeImage = async (image: HTMLImageElement) => {
  if (typeof image.decode !== 'function') return;
  try {
    await image.decode();
  } catch {
    // Decode failures follow the same fail-open readiness path as load errors.
  }
};
export const useHeroEntranceReady = t0 => {
  const $ = _c(29);
  const {
    failOpenMs: t1,
    hasCarMedia,
    hasCriticalMedia,
    hasEntranceReveal,
    onReady
  } = t0;
  const failOpenMs = t1 === undefined ? ENTRANCE_FAIL_OPEN_MS : t1;
  const [isReady, setIsReady] = useState(false);
  const criticalImageRef = useRef(null);
  const carImageRef = useRef(null);
  const firstFrameRef = useRef(null);
  const secondFrameRef = useRef(null);
  const hasCommittedRef = useRef(false);
  const isMountedRef = useRef(false);
  const isCriticalReadyRef = useRef(!hasCriticalMedia);
  const isCarReadyRef = useRef(!hasCarMedia);
  let t2;
  if ($[0] !== onReady) {
    t2 = () => {
      if (!isMountedRef.current || hasCommittedRef.current) {
        return;
      }
      hasCommittedRef.current = true;
      setIsReady(true);
      markSpartanProductHeroReady();
      safeCall(onReady);
    };
    $[0] = onReady;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const commitReady = t2;
  let t3;
  if ($[2] !== commitReady) {
    t3 = t4 => {
      const force = t4 === undefined ? false : t4;
      if (hasCommittedRef.current || firstFrameRef.current !== null || !force && (!isCriticalReadyRef.current || !isCarReadyRef.current)) {
        return;
      }
      firstFrameRef.current = window.requestAnimationFrame(() => {
        secondFrameRef.current = window.requestAnimationFrame(() => {
          firstFrameRef.current = null;
          secondFrameRef.current = null;
          commitReady();
        });
      });
    };
    $[2] = commitReady;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const scheduleReady = t3;
  let t4;
  if ($[4] !== scheduleReady) {
    t4 = () => {
      isCriticalReadyRef.current = true;
      scheduleReady();
    };
    $[4] = scheduleReady;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const markCriticalReady = t4;
  let t5;
  if ($[6] !== scheduleReady) {
    t5 = () => {
      isCarReadyRef.current = true;
      scheduleReady();
    };
    $[6] = scheduleReady;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const markCarReady = t5;
  let t6;
  if ($[8] !== markCriticalReady) {
    t6 = image => {
      decodeImage(image).then(markCriticalReady);
    };
    $[8] = markCriticalReady;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const decodeCriticalImage = t6;
  let t7;
  if ($[10] !== markCarReady) {
    t7 = image_0 => {
      decodeImage(image_0).then(markCarReady);
    };
    $[10] = markCarReady;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const decodeCarImage = t7;
  let t8;
  if ($[12] !== decodeCriticalImage) {
    t8 = event => {
      decodeCriticalImage(event.currentTarget);
    };
    $[12] = decodeCriticalImage;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const handleCriticalMediaLoad = t8;
  let t9;
  if ($[14] !== decodeCarImage) {
    t9 = event_0 => {
      decodeCarImage(event_0.currentTarget);
    };
    $[14] = decodeCarImage;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleCarMediaLoad = t9;
  let t10;
  let t11;
  if ($[16] !== decodeCarImage || $[17] !== decodeCriticalImage || $[18] !== failOpenMs || $[19] !== hasEntranceReveal || $[20] !== scheduleReady) {
    t10 = () => {
      isMountedRef.current = true;
      clearSpartanProductHeroReady();
      const failOpenTimeoutId = window.setTimeout(() => scheduleReady(true), failOpenMs);
      const hasReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (!hasEntranceReveal || hasReducedMotion) {
        scheduleReady(true);
      } else {
        if (criticalImageRef.current?.complete) {
          decodeCriticalImage(criticalImageRef.current);
        }
        if (carImageRef.current?.complete) {
          decodeCarImage(carImageRef.current);
        }
        scheduleReady();
      }
      return () => {
        isMountedRef.current = false;
        window.clearTimeout(failOpenTimeoutId);
        clearSpartanProductHeroReady();
        if (firstFrameRef.current !== null) {
          window.cancelAnimationFrame(firstFrameRef.current);
          firstFrameRef.current = null;
        }
        if (secondFrameRef.current !== null) {
          window.cancelAnimationFrame(secondFrameRef.current);
          secondFrameRef.current = null;
        }
      };
    };
    t11 = [decodeCarImage, decodeCriticalImage, failOpenMs, hasEntranceReveal, scheduleReady];
    $[16] = decodeCarImage;
    $[17] = decodeCriticalImage;
    $[18] = failOpenMs;
    $[19] = hasEntranceReveal;
    $[20] = scheduleReady;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  useEffect(t10, t11);
  let t12;
  if ($[23] !== handleCarMediaLoad || $[24] !== handleCriticalMediaLoad || $[25] !== isReady || $[26] !== markCarReady || $[27] !== markCriticalReady) {
    t12 = {
      carImageRef,
      criticalImageRef,
      handleCarMediaError: markCarReady,
      handleCarMediaLoad,
      handleCriticalMediaError: markCriticalReady,
      handleCriticalMediaLoad,
      isReady
    };
    $[23] = handleCarMediaLoad;
    $[24] = handleCriticalMediaLoad;
    $[25] = isReady;
    $[26] = markCarReady;
    $[27] = markCriticalReady;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
};